import { EntityInfo, EntityInfoUtils } from '@record-it-npm/synchro-common';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { EntityStorage } from '../EntityStorage/EntityStorage';
import { EntityManagerBaseEntity } from '../EntityManagerBaseEntity';
import { EntityManagerEntityTypesByEntityName } from '../EntityManagerEntityTypesByEntityName';
import { AnySynchronizationEnvironmentTypesConstraint } from '../SynchronizationEnvironmentTypes';

export class EntityCollection<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityName extends
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> {
  private entitiesById = new Map<
    string,
    TEntityTypesByEntityName[TEntityName]['entity']
  >();
  private lastTemporaryGroupNameByEntityId = new Map<string, string | null>();
  private entitiesLoaded: boolean = false;

  constructor(
    private readonly entityInfo: EntityInfo<
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'],
      TEntityName,
      TEntityTypesByEntityName[TEntityName]['entity']
    >,
    private readonly entityStorage: EntityStorage<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName
    >,
    private readonly options: EntityCollectionOptions
  ) {}

  public async loadEntities({
    patchEntity
  }: LoadEntitiesOptions<
    TSynchronizationEnvironmentTypes,
    TEntityName,
    TEntityTypesByEntityName
  >): Promise<void> {
    const entities = await this.entityStorage.loadEntities(this.entityInfo);
    for (const entity of entities) {
      EntityInfoUtils.applyDefaultValues<
        TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'],
        TEntityName,
        any
      >(this.entityInfo, entity);
      patchEntity({ entity });
      this.entitiesById.set(entity.id, entity);
      this.lastTemporaryGroupNameByEntityId.set(
        entity.id,
        entity.temporaryGroupName
      );
    }

    this.entitiesLoaded = true;
  }

  public addExternalEntity(
    entity: Partial<TEntityTypesByEntityName[TEntityName]['entity']>
  ): TEntityTypesByEntityName[TEntityName]['entity'] {
    assertNotNullOrUndefined(
      entity.id,
      "can't add an external entity without an id"
    );
    this.explicitlyMakeExternalEntityNotOnlyLocal(entity);

    const existingEntities = this.getExistingEntities(
      entity.id,
      entity.originalIds ?? []
    );
    const firstExistingEntity = existingEntities[0];

    let addedEntity;

    if (firstExistingEntity) {
      addedEntity = this.add({
        ...firstExistingEntity,
        ...entity
      });
    } else {
      EntityInfoUtils.applyDefaultValues<
        TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'],
        TEntityName,
        any
      >(this.entityInfo, entity);

      // let's just assume that external entities are sent correctly to us
      // this is not entirely correct but sadly old data is not stored correctly in the Database yet
      // only validate the entity so we at least can log wrong entities
      EntityInfoUtils.validateEntityWithLoggingOnly<
        TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'],
        TEntityName,
        any
      >(this.entityInfo, entity);

      addedEntity = this.add(
        entity as TEntityTypesByEntityName[TEntityName]['entity']
      );
    }

    return addedEntity;
  }

  public update(
    entity: TEntityTypesByEntityName[TEntityName]['entity']
  ): TEntityTypesByEntityName[TEntityName]['entity'] {
    return this.conditionallyAddEntity(
      entity,
      (existingEntities) => !!existingEntities.length
    );
  }

  public add(
    entity: TEntityTypesByEntityName[TEntityName]['entity']
  ): TEntityTypesByEntityName[TEntityName]['entity'] {
    return this.conditionallyAddEntity(entity, () => true);
  }

  public remove(entity: TEntityTypesByEntityName[TEntityName]['entity']): void {
    this.assertEntitiesLoaded();

    this.removeById(entity.id);
  }

  public getAll(): Array<TEntityTypesByEntityName[TEntityName]['entity']> {
    this.assertEntitiesLoaded();

    return Array.from(this.entitiesById.values());
  }

  public getAllById(): Map<
    string,
    TEntityTypesByEntityName[TEntityName]['entity']
  > {
    return this.entitiesById;
  }

  public getById(
    id: string
  ): TEntityTypesByEntityName[TEntityName]['entity'] | null {
    this.assertEntitiesLoaded();

    return this.entitiesById.get(id) ?? null;
  }

  public getByIdOrOriginalId(
    anyId: EntityManagerBaseEntity['id']
  ): TEntityTypesByEntityName[TEntityName]['entity'] | null {
    const entityById = this.getById(anyId);
    if (entityById) return entityById;

    return this.getByOriginalId(anyId);
  }

  /**
   * @performance ideally we would have a map from the originalId to the entity itself
   */
  public getByOriginalId(
    originalId: EntityManagerBaseEntity['id']
  ): TEntityTypesByEntityName[TEntityName]['entity'] | null {
    this.assertEntitiesLoaded();

    return (
      this.getAll().find((e) => e.originalIds.includes(originalId)) ?? null
    );
  }

  public clear(): void {
    this.entitiesById.clear();
  }

  /**
   * @param entity
   * @param errorMessagePrefix - example: "can\'t Worker.handleInput because processTaskGroup"
   */
  public assertEntityIsTracked(
    entity: TEntityTypesByEntityName[TEntityName]['entity'] | null,
    errorMessagePrefix: string
  ): asserts entity is TEntityTypesByEntityName[TEntityName]['entity'] {
    assertNotNullOrUndefined(entity, `${errorMessagePrefix} is null`);

    if (!this.entityIsTracked(entity)) {
      throw new Error(`${errorMessagePrefix} is not tracked`);
    }
  }

  /**
   * Useful if e.g. you want to create sub entities locally, only if the entity is still tracked
   */
  public entityIsTracked(
    entity: TEntityTypesByEntityName[TEntityName]['entity'] | null
  ): boolean {
    if (!entity) {
      return false;
    }

    if (this.getById(entity.id)) {
      return true;
    }

    if (entity.onlyLocal && this.getByOriginalId(entity.id)) {
      return true;
    }

    return false;
  }

  private conditionallyAddEntity(
    entity: TEntityTypesByEntityName[TEntityName]['entity'],
    conditionCallback: (
      existingEntities: Array<TEntityTypesByEntityName[TEntityName]['entity']>
    ) => boolean
  ): TEntityTypesByEntityName[TEntityName]['entity'] {
    this.assertEntitiesLoaded();

    const existingEntities = this.getExistingEntities(
      entity.id,
      entity.originalIds
    );
    const idsToRemove = existingEntities
      .filter((e) => e.id !== entity.id)
      .map((e) => e.id);

    if (conditionCallback(existingEntities)) {
      const addedEntity = this.addEntityToEntitiesById(
        entity,
        existingEntities
      );
      this.storeEntity(addedEntity);

      idsToRemove.forEach((id) => {
        this.removeById(id);
      });

      return addedEntity;
    }

    return entity;
  }

  private storeEntity(
    entity: TEntityTypesByEntityName[TEntityName]['entity']
  ): void {
    const oldTemporaryGroupName =
      this.lastTemporaryGroupNameByEntityId.get(entity.id) ?? null;

    if (entity.temporaryGroupName == null) {
      this.entityStorage.saveEntity(this.entityInfo, entity);
    } else if (oldTemporaryGroupName == null) {
      // entity has been downgraded from a normal entity to a temporary entity
      this.entityStorage.deleteEntity(this.entityInfo, entity.id);
    }

    this.lastTemporaryGroupNameByEntityId.set(
      entity.id,
      entity.temporaryGroupName
    );
  }

  private removeById(id: EntityManagerBaseEntity['id']): void {
    const entityRemoved = this.entitiesById.delete(id);
    this.lastTemporaryGroupNameByEntityId.delete(id);

    if (entityRemoved) {
      this.entityStorage.deleteEntity(this.entityInfo, id);
    }
  }

  private getExistingEntities(
    entityId: string,
    originalIds: Array<string>
  ): Array<TEntityTypesByEntityName[TEntityName]['entity']> {
    const ids = [entityId, ...originalIds];
    const entities = [];

    for (const id of ids) {
      const entity = this.getById(id);
      if (entity) {
        entities.push(entity);
      }
    }

    return entities;
  }

  private addEntityToEntitiesById(
    entity: TEntityTypesByEntityName[TEntityName]['entity'],
    existingEntities: Array<TEntityTypesByEntityName[TEntityName]['entity']>
  ): TEntityTypesByEntityName[TEntityName]['entity'] {
    const firstExistingEntity = existingEntities[0];

    let storedEntity;
    if (firstExistingEntity) {
      storedEntity = this.mergeEntities(firstExistingEntity, entity);
      this.entitiesById.set(entity.id, storedEntity);
    } else {
      storedEntity = entity;
      this.entitiesById.set(entity.id, entity);
    }

    return storedEntity;
  }

  private mergeEntities(
    a: TEntityTypesByEntityName[TEntityName]['entity'],
    b: TEntityTypesByEntityName[TEntityName]['entity']
  ): TEntityTypesByEntityName[TEntityName]['entity'] {
    if (this.options.preserveInstances) {
      return Object.assign(a, b);
    }

    return { ...a, ...b };
  }

  private assertEntitiesLoaded(): void {
    if (!this.entitiesLoaded) {
      throw new Error(
        "can't use the EntityCollection because the entities are not loaded"
      );
    }
  }

  /**
   * External entities are never onlyLocal, but they potentially don't have the onlyLocal property set (it can be undefined).
   * The onlyLocal property gets set to false explicitly, so it can override the value of the existing entity
   * @param entity
   */
  private explicitlyMakeExternalEntityNotOnlyLocal(
    entity: Partial<TEntityTypesByEntityName[TEntityName]['entity']>
  ): void {
    if (entity.onlyLocal) {
      console.error('onlyLocal external entities are not supported');
    }
    entity.onlyLocal = false;
  }
}

export type EntityCollectionOptions = {
  preserveInstances: boolean;
};

export type LoadEntitiesOptions<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityName extends
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> = {
  patchEntity: (
    options: PatchEntityOptions<
      TSynchronizationEnvironmentTypes,
      TEntityName,
      TEntityTypesByEntityName
    >
  ) => void;
};

export type PatchEntityOptions<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityName extends
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> = {
  entity: TEntityTypesByEntityName[TEntityName]['entity'];
};
